import { IndexedSet } from '../../utils/indexedSet';
import { ChangeContext, emptyChangeContext } from '../../client/changeManager';
import {
  ResolverState,
  toLoading,
} from '../../resolvers/definition/resolverState';
import type { TagCalculationResult } from '../../links/discovery/calculateTags';

export type CacheMeta<TParams> = {
  changeContext: ChangeContext;
  hash?: unknown;
  params: TParams;
  paramLinkTags: IndexedSet<string> | null;
  // Data-derived link tags + per-tag contexts, refreshed from the node's data
  // on each syncTags. Lives on the node so it's GC'd with the entry and is
  // available to link propagation without a separate registry.
  dataLinkTags?: TagCalculationResult | null;
  lastChange?: { dataChanged: boolean; flagsChanged: boolean };
  // Wall-clock time the data was last modified (i.e. last dataChanged). Anchored
  // to the genuine modification, so it travels with the value through peer-sync
  // hops and persistence rather than resetting on each copy.
  storedAt?: number;
  settledAt?: number;
  failedAt?: number;
  visible: boolean;
};

export type CacheLifecycle = {
  attachedSince?: number;
  validUntil?: number;
  queuedAt?: number;
};

export type CacheEntry<TParams, TData> = {
  state: ResolverState<TData>;
  meta: CacheMeta<TParams>;
  lifecycle: CacheLifecycle;
};

export const createEntry = <TParams, TData>({
  state,
  meta,
}: {
  state: ResolverState<TData>;
  meta: {
    params: TParams;
    paramLinkTags: IndexedSet<string> | null;
    dataLinkTags?: TagCalculationResult | null;
    hash?: unknown;
    changeContext?: ChangeContext;
    storedAt?: number;
    visible?: boolean;
  };
}): CacheEntry<TParams, TData> => ({
  state,
  meta: {
    changeContext: emptyChangeContext,
    visible: true,
    ...meta,
  },
  lifecycle: {},
});

export const createEmptyEntry = <TParams, TData>(
  params: TParams,
  paramLinkTags: IndexedSet<string> | null,
  changeContext: ChangeContext = emptyChangeContext
): CacheEntry<TParams, TData> =>
  createEntry({
    state: toLoading()<TData>(),
    meta: {
      params,
      paramLinkTags,
      changeContext,
    },
  });
